import Observable from 'zen-observable';
import { Hub } from 'aws-amplify';
import API, { BookMeetingResponse, EVENT, FilterReplays, MEETING, PROTECTED, REPLAYS, SESSION } from '../../api';
import { registerMeetingCompleteSubscription } from '../../../config/appsync';
import { OnRegisterToMeetingCompletedSubscription } from '../../../config/appsync/API';
import { AttendeeUrlProcessingPolling } from '../polling/attendee-url-processing-polling';
import { THIRTY_SECONDS_TIMEOUT, JOIN_STATUS } from '../../constants';
import { RegistrationResultType } from '../../../hooks/use-booking-subscription';
import { isCapEnabledAttendResponse } from '../../helpers/openMeetingRegistrationHelper';

type SubscriptionData = { value: { data: OnRegisterToMeetingCompletedSubscription }};

export type OpenMeetingRegistrationOutcome = 'registered' | 'unavailable' | 'failed';

export type OpenMeetingRegistrationResult = {
  outcome: OpenMeetingRegistrationOutcome;
  usedCapFlow: boolean;
};

const mapRegistrationResult = (registrationResult: string | null | undefined): OpenMeetingRegistrationOutcome => {
  if (registrationResult === RegistrationResultType.UNAVAILABLE) {
    return 'unavailable';
  }
  if (registrationResult === RegistrationResultType.FAILED) {
    return 'failed';
  }
  return 'registered';
};

const createRegistrationWaiter = (response: BookMeetingResponse): {
  waitForAck: () => Promise<void>;
  waitForResult: () => Promise<OpenMeetingRegistrationOutcome>;
} => {
  let resolveAck: () => void = () => undefined;
  const ackPromise = new Promise<void>((resolve) => {
    resolveAck = resolve;
  });

  const resultPromise = new Promise<OpenMeetingRegistrationOutcome>((resolve) => {
    let timeoutId: number | null = null;
    let hubListenerRemover: (() => void) | null = null;
    let activePolling: AttendeeUrlProcessingPolling | null = null;

    const cleanup = (client?: { unsubscribe(): void, closed: boolean }) => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (hubListenerRemover) {
        hubListenerRemover();
        hubListenerRemover = null;
      }
      if (activePolling) {
        activePolling.stop();
        activePolling = null;
      }
      if (client && !client.closed) {
        client.unsubscribe();
      }
    };

    const complete = (outcome: OpenMeetingRegistrationOutcome, client?: { unsubscribe(): void, closed: boolean }) => {
      cleanup(client);
      resolve(outcome);
    };

    const startPollingFallback = (client: { unsubscribe(): void, closed: boolean }) => {
      activePolling = new AttendeeUrlProcessingPolling(response.registrationId, response.appSyncToken);
      activePolling.start()
        .then(pollingResponse => {
          complete(mapRegistrationResult(pollingResponse.registrationResult as string), client);
        })
        .catch(() => complete('failed', client));
      activePolling.subscribeToPollingStart(resolveAck);
    };

    const client = (registerMeetingCompleteSubscription(
      response.registrationId,
      response.appSyncToken
    ) as unknown as Observable<SubscriptionData>)
      .subscribe({
        next({ value }: SubscriptionData): void {
          const registrationResult = value?.data?.onRegisterToMeetingCompleted?.registrationResult;
          if (registrationResult) {
            complete(mapRegistrationResult(registrationResult as string), client);
          }
          client.unsubscribe();
        },
        error(): void {
          if (!client.closed) client.unsubscribe();
          startPollingFallback(client);
        }
      });

    timeoutId = window.setTimeout(() => {
      if (!client.closed) client.unsubscribe();
      complete('failed', client);
    }, THIRTY_SECONDS_TIMEOUT);

    hubListenerRemover = Hub.listen('api', ({ payload }) => {
      const { event, data } = payload;
      if (event === 'Subscription ack') {
        resolveAck();
        return;
      }
      if (data?.connectionState === 'ConnectionDisrupted') {
        if (!client.closed) client.unsubscribe();
        startPollingFallback(client);
      }
    });
  });

  return {
    waitForAck: () => ackPromise,
    waitForResult: () => resultPromise,
  };
};

export type OpenMeetingRegistrationOptions = {
  audienceCapacityEnabled?: boolean;
};

export type RefreshMeetingJoinStatusSource = 'session' | 'replays';

export async function refreshMeetingJoinStatus(
  eventId: string,
  meetingId: string,
  source: RefreshMeetingJoinStatusSource = 'session'
): Promise<string | undefined> {
  const { fetchMeetingPrivateSessionList, fetchReplaysList } = API[PROTECTED];

  if (source === 'session') {
    const meetings = await fetchMeetingPrivateSessionList(EVENT, eventId, SESSION);
    return meetings.find(meeting => meeting.meetingId === meetingId)?.joinStatus;
  }

  for (const filter of [FilterReplays.UpcomingReplays, FilterReplays.PastReplays]) {
    const meetings = await fetchReplaysList(EVENT, eventId, MEETING, REPLAYS, filter);
    const meeting = meetings.find(item => item.meetingId === meetingId);
    if (meeting) {
      return meeting.joinStatus;
    }
  }

  return undefined;
}

export async function isMeetingRegistrationFullAfterLeave(
  eventId: string,
  meetingId: string,
  source: RefreshMeetingJoinStatusSource = 'session'
): Promise<boolean> {
  const refreshedJoinStatus = await refreshMeetingJoinStatus(eventId, meetingId, source);
  return refreshedJoinStatus === JOIN_STATUS.REGISTRATION_FULL;
}

export async function registerForOpenMeeting(
  eventId: string,
  meetingId: string,
  options?: OpenMeetingRegistrationOptions
): Promise<OpenMeetingRegistrationResult> {
  const { attendMeeting, initiateAttend } = API[PROTECTED];
  const useCapFlow = options?.audienceCapacityEnabled === true;

  try {
    const response = await attendMeeting(eventId, meetingId);

    if (!useCapFlow || !isCapEnabledAttendResponse(response)) {
      return { outcome: 'registered', usedCapFlow: false };
    }

    const { waitForAck, waitForResult } = createRegistrationWaiter(response);
    await waitForAck();
    await initiateAttend(eventId, meetingId, response.registrationId);
    return { outcome: await waitForResult(), usedCapFlow: true };
  } catch (error) {
    const status = (error as { status?: number })?.status;
    if (status === 400) {
      return { outcome: 'unavailable', usedCapFlow: useCapFlow };
    }
    return { outcome: 'failed', usedCapFlow: useCapFlow };
  }
}
